import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
  Stack,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface ImageUploadWithPreviewProps {
  imageFile?: File | null;
  existingImageUrl?: string;
  onImageChange: (file: File | null) => void;
  label?: string;
  disabled?: boolean;
}

export const ImageUploadWithPreview: React.FC<ImageUploadWithPreviewProps> = ({
  imageFile,
  existingImageUrl,
  onImageChange,
  label = "Upload Image",
  disabled = false,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create preview URL when imageFile changes
  React.useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url); // Cleanup
    } else {
      setPreviewUrl(null);
    }
  }, [imageFile]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    onImageChange(file);
  };

  const handleRemoveImage = () => {
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const displayImage = previewUrl || existingImageUrl;

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      
      {displayImage ? (
        <Card sx={{ mb: 2, maxWidth: { xs: "100%", sm: 400 } }}>
          <CardContent sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 250, md: 300 },
                  borderRadius: 2,
                  overflow: "hidden",
                  position: "relative",
                  backgroundColor: "#f5f5f5",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)"
                  }
                }}
              >
                <img
                  src={displayImage}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
              
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUploadClick}
                  disabled={disabled}
                  sx={{ flex: 1 }}
                >
                  Change Image
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleRemoveImage}
                  disabled={disabled}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Card
          sx={{
            mb: 2,
            maxWidth: { xs: "100%", sm: 400 },
            border: "2px dashed #ccc",
            backgroundColor: "#fafafa",
            cursor: disabled ? "default" : "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": disabled ? {} : { 
              borderColor: "#1976d2", 
              backgroundColor: "#f0f8ff",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)"
            },
          }}
          onClick={disabled ? undefined : handleUploadClick}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
            <Stack spacing={2} alignItems="center">
              <ImageIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: "#ccc" }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Click to upload image
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PNG, JPG, GIF up to 10MB
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      <input
        ref={fileInputRef}
        hidden
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        disabled={disabled}
      />
    </Box>
  );
};
